import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import 'leaflet/dist/leaflet.css';
import { MapContainer, GeoJSON, Marker, Popup, TileLayer } from "react-leaflet";
import L from 'leaflet';
import Layout from '../components/Layout';
import MapData from './kad.json';
import { httpGet } from "../services/https";

// Fix Leaflet marker icon issue in Webpack/React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const ODFMap = () => {
  const [lgStat, setLgStat] = useState({});
  const [lgaDetails, setLgaDetails] = useState({});
  const [mappedCommunities, setMappedCommunities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMapData = async () => {
      try {
        // Fetch summary
        const summaryRes = await httpGet('/odf/odf');
        const stats = {};
        const details = {};

        if (Array.isArray(summaryRes)) {
          summaryRes.forEach((item) => {
            const comms = parseInt(item.no_of_communities) || 0;
            const cert = parseInt(item.no_of_certified) || 0;
            const trig = parseInt(item.no_of_triggered) || 0;
            const pct = comms > 0 ? Math.round((cert / comms) * 100) : 0;

            stats[item.lga] = pct;
            details[item.lga] = {
              communities: comms,
              certified: cert,
              triggered: trig,
              pct
            };
          });
        }
        setLgStat(stats);
        setLgaDetails(details);

        // Fetch communities with GPS coordinates
        const commRes = await httpGet('/odf/cds/communities?limit=500');
        if (commRes && commRes.status && Array.isArray(commRes.data)) {
          const withCoords = commRes.data.filter(c => c.latitude && c.longitude && !isNaN(c.latitude) && !isNaN(c.longitude));
          setMappedCommunities(withCoords);
        }
      } catch (error) {
        console.error('Error fetching map data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMapData();
  }, []);

  const handleOnEach = (feature, layer) => {
    const lgaName = feature?.properties?.admin2Name || feature?.properties?.name;
    const detail = lgaDetails[lgaName] || {};
    const pct = lgStat[lgaName] !== undefined ? lgStat[lgaName] : 0;

    const popupContent = `
      <div style="font-family: sans-serif; padding: 4px;">
        <h4 style="margin: 0 0 6px 0; font-size: 16px; font-weight: bold; color: #1e293b;">${lgaName} LGA</h4>
        <p style="margin: 2px 0; font-size: 12px; color: #475569;"><b>Total Communities:</b> ${detail.communities || 'N/A'}</p>
        <p style="margin: 2px 0; font-size: 12px; color: #475569;"><b>Triggered Communities:</b> ${detail.triggered || 0}</p>
        <p style="margin: 2px 0; font-size: 12px; color: #059669;"><b>Certified ODF:</b> ${detail.certified || 0}</p>
        <p style="margin: 4px 0 0 0; font-size: 13px; font-weight: bold; color: #2563eb;"><b>Certification Rate:</b> ${pct}%</p>
      </div>
    `;

    layer.bindPopup(popupContent);

    const fillColor = pct >= 70 ? '#10b981' : pct >= 40 ? '#f59e0b' : '#ef4444';
    layer.options.fillColor = fillColor;
    layer.options.fillOpacity = 0.6;
    layer.options.color = '#334155';
    layer.options.weight = 1.5;
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Kaduna State ODF Map</h1>
            <p className="text-sm text-gray-500 mt-1">
              Geographic visualization of Open Defecation Free (ODF) certification rates across LGAs
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/odf/status"
              className="inline-flex items-center px-4 py-2 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-medium rounded-lg transition text-xs"
            >
              📋 ODF Directory & Table
            </Link>
            <Link
              to="/odf/add"
              className="inline-flex items-center px-4 py-2 bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium rounded-lg transition text-xs"
            >
              📤 Upload / Add CDS Data
            </Link>
            <div className="flex items-center gap-3 text-xs font-semibold pl-2 border-l">
              <div className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-emerald-500 inline-block"></span>
                <span className="text-gray-700">High (&ge;70%)</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-amber-500 inline-block"></span>
                <span className="text-gray-700">Med (40-69%)</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-rose-500 inline-block"></span>
                <span className="text-gray-700">Low (&lt;40%)</span>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="bg-white p-12 rounded-xl text-center text-gray-500">Loading map boundaries and CDS data...</div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <MapContainer style={{ height: '75vh', width: '100%' }} zoom={8.5} center={[10.2, 7.8]}>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <GeoJSON data={MapData.features} onEachFeature={handleOnEach} />
              {mappedCommunities.map((comm) => (
                <Marker key={comm.id} position={[comm.latitude, comm.longitude]}>
                  <Popup>
                    <div style={{ fontFamily: 'sans-serif', padding: '4px' }}>
                      <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: 'bold' }}>{comm.community_name}</h4>
                      <p style={{ margin: '2px 0', fontSize: '12px', color: '#64748b' }}>Ward: {comm.ward} ({comm.lga} LGA)</p>
                      <p style={{ margin: '2px 0', fontSize: '12px' }}>
                        ODF: <b style={{ color: String(comm.odf_certified).toUpperCase() === 'YES' ? '#059669' : '#dc2626' }}>
                          {comm.odf_certified}
                        </b>
                      </p>
                      <p style={{ margin: '2px 0', fontSize: '12px' }}>Improved Latrines: {comm.improved_hh_latrines || 0}</p>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ODFMap;